import React, { useState } from 'react';
import { subscriptionService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import Card from './ui/Card';
import Button from './ui/Button';

const CancelSubscription: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Vérifier si l'utilisateur a un plan payant
  const hasPaidPlan = user?.plan && user.plan.price > 0;
  
  // Vérifier si l'abonnement est déjà en cours d'annulation
  const isCanceling = user?.subscriptionStatus === 'canceling';
  
  // Formater la date de fin d'abonnement si elle existe
  const formatEndDate = () => {
    if (user?.subscriptionEndDate) {
      const endDate = new Date(user.subscriptionEndDate);
      return endDate.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    return null;
  };

  const handleCancelClick = () => {
    setShowConfirmation(true);
    setError(null);
  };

  const handleCancelConfirm = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      const result = await subscriptionService.cancelSubscription();
      
      if (result.success) {
        setSuccess('Votre abonnement a été annulé avec succès. Vous serez désormais sur le plan gratuit à la fin de votre période de facturation.');
        setShowConfirmation(false);
        
        // Mettre à jour les informations utilisateur dans le contexte
        if (updateUser && result.user) {
          updateUser(result.user);
        }
      } else {
        setError('Une erreur est survenue lors de l\'annulation de votre abonnement.');
      }
    } catch (err: any) {
      console.error('Erreur lors de l\'annulation de l\'abonnement:', err);
      setError(err.response?.data?.error || 'Une erreur est survenue lors de l\'annulation de votre abonnement.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelAbort = () => {
    setShowConfirmation(false);
  };

  if (!hasPaidPlan) {
    return (
      <Card title="Gestion de l'abonnement">
        <p className="text-gray-600 mb-4">
          Vous êtes actuellement sur le plan gratuit. Aucun abonnement à annuler.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Gestion de l'abonnement">
      {success ? (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      ) : (
        <>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          {!showConfirmation ? (
            <div>
              <p className="text-gray-600 mb-4">
                Vous êtes actuellement abonné au plan <span className="font-semibold">{user?.plan?.name}</span>.
              </p>
              
              {isCanceling && user?.subscriptionEndDate && (
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-4">
                  Votre abonnement a été annulé mais reste actif jusqu'au {formatEndDate()}. 
                  Après cette date, votre compte sera automatiquement rétrogradé vers le plan gratuit.
                </div>
              )}
              
              {!isCanceling && (
                <Button 
                  variant="danger" 
                  onClick={handleCancelClick}
                  disabled={isSubmitting}
                >
                  Annuler mon abonnement
                </Button>
              )}  
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded">
              <h3 className="text-lg font-medium text-yellow-900 mb-2">Êtes-vous sûr de vouloir annuler votre abonnement ?</h3>
              <p className="text-yellow-700 mb-4">
                En annulant votre abonnement, vous conserverez l'accès à votre plan actuel jusqu'à la fin de la période de facturation en cours. 
                Ensuite, votre compte sera automatiquement rétrogradé vers le plan gratuit avec des fonctionnalités limitées.
              </p>
              <div className="flex space-x-3">
                <Button 
                  variant="danger" 
                  onClick={handleCancelConfirm}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Annulation en cours...' : 'Confirmer l\'annulation'}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={handleCancelAbort}
                  disabled={isSubmitting}
                >
                  Retour
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default CancelSubscription;
